import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import SignUpPage from './components/SignUpPage';
import SignInPage from './components/SignInPage';
import ProtectedRoute from './components/ProtectedRoute';
import Team from './components/Team';
import ResetPassword from './components/ResetPassword';
import ForgotPassword from './components/ForgotPassword';
import AdminSalesPage from './components/admin/AdminSales';
import AdminCustomerList from './components/admin/AdminCustomerList';
import AdminProductPage from './components/admin/AdminProduct';
import UserSales from './components/BillingHistory';
import RolePermissionPage from './components/admin/AdminRolePermission';
import CompleteProfilePage from './components/CompletedProfile';
import SetupProfilePage from './components/SetupProfilePage';
import ApiDocsPage from './components/ApiDocsPage';
import PaymentCancel from './components/PaymentCancel';
import PaymentSuccess from './components/PaymentSuccess';
import { EditFilings } from './components/EditFilings';
import { ViewFilings } from './components/ViewFilings';
import { ErrorBoundary } from './components/ErrorBoundary';
import AdminReferralPage from './components/admin/AdminReferral';
import AdminOrganizationFilings from './components/admin/AdminOrganizationFilings';
import AdminEditCustomer from './components/admin/AdminEditCustomer';
import AdminTodayFilings from './components/admin/AdminTodayFilings';
import ResumePayment from './components/ResumePayment';
import CompanyProfile from './components/CompanyProfile';
import LearnRapidTagger from './components/LearnRapidTagger';
import PartnerIntegration from './components/APIIntegration';
import NoOrganizationNotice from './components/NoOrganizationNotice';
import { useSession } from './contexts/SessionContext';
import { useCompany } from './contexts/CompanyContext';
import { useEffect } from 'react';
import { ChatwootWidget } from './components/ChatwootWidget';
import AdminSalesAnalytics from './components/admin/AdminSalesAnalytics';
import AdminUsageAnalytics from './components/admin/AdminUsageAnalytics';
import AdminRefundLogs from './components/admin/AdminRefundLogs';
import AdminAnnouncements from './components/admin/AdminAnnouncements';
import AdminMsic from './components/admin/AdminMsic';
import CountryBasedFilingRoute from './components/singapore/CountryBasedFilingRoute';
import CountryBasedUsageLogsRoute from './components/singapore/CountryBasedUsageLogsRoute';
import AdminOrganizationUsage from './components/admin/AdminOrganizationUsage';
import AdminSalesman from './components/admin/AdminSalesman';

// Component to handle "/" route - redirects based on auth status
const HomeRedirect: React.FC = () => {
  const { user, loading, orgs } = useSession();
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;

    const orgId = selectedCompany?.company_id || orgs[0]?.orgId;
    if (user && orgId) {
      navigate(`/all-filings/${orgId}`, { replace: true });
      return;
    }
    if (user) return;
    navigate('/signin', { replace: true });
  }, [user, loading, selectedCompany?.company_id, orgs, navigate]);

  // Show loading while determining redirect
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4">
        <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="text-gray-600 text-sm">Loading...</p>
      </div>
    </div>
  );
};

// Standard behavior: if user is already signed in and hits /signin, redirect them.
// Do NOT sign them out automatically (avoids races and matches typical apps).
const SignInRoute: React.FC = () => {
  const { user, loading, orgs } = useSession();
  const { selectedCompany } = useCompany();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    const orgId = selectedCompany?.company_id || orgs[0]?.orgId;
    if (orgId) {
      return <Navigate to={`/all-filings/${orgId}`} replace />;
    }
    return <Navigate to="/no-organization" replace />;
  }

  return <SignInPage />;
};

const AppRoutes: React.FC = () => {
  const { user } = useSession();
  return (
    <>
      {user && <ChatwootWidget />}
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/signin" element={<SignInRoute />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/complete-profile" element={<CompleteProfilePage />} />
        <Route path="/setup-profile" element={<SetupProfilePage />} />
        <Route path="/Reset-Password" element={<ResetPassword />} />
        <Route path="/Forgot-Password" element={<ForgotPassword />} />
        <Route path="/usage-logs/:companyId" element={<CountryBasedUsageLogsRoute />} />
        <Route path="/no-organization" element={<NoOrganizationNotice />} />
        <Route
            path="/all-filings/:companyId/edit/:filingId"
            element={
            <ProtectedRoute>
                <ErrorBoundary>
                    <EditFilings />
                </ErrorBoundary>
            </ProtectedRoute>
            }
        />
        <Route
            path="/all-filings/:companyId/view/:filingId"
            element={
            <ProtectedRoute>
                <ViewFilings />
            </ProtectedRoute>
            }
        />
        <Route
            path="/settings/:companyId"
            element={
            <ProtectedRoute>
                <CompanyProfile />
            </ProtectedRoute>
            }
        />
        <Route
            path="/team/:companyId"
            element={
            <ProtectedRoute>
                <Team />
            </ProtectedRoute>
            }
        />
        <Route
            path="/all-filings/:companyId"
            element={
            <ProtectedRoute>
                <CountryBasedFilingRoute />
            </ProtectedRoute>
            }
        />
        <Route
            path="/company-profile/:companyId"
            element={
            <ProtectedRoute>
                <CompanyProfile />
            </ProtectedRoute>
            }
        />

        <Route 
          path="/resume-payment"
          element={<ResumePayment />}
        />

        <Route
            path="/user-sales/:companyId"
            element={
            <ProtectedRoute>
                <UserSales />
            </ProtectedRoute>
            }
        />

        <Route
          path="/learn-rapid-tagger"
          element={
            <ProtectedRoute>
              <LearnRapidTagger />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sales"
          element={
            <ProtectedRoute>
              <AdminSalesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/organizations/:orgId/usage"
          element={
            <ProtectedRoute>
              <AdminOrganizationUsage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/salesmen"
          element={
            <ProtectedRoute>
              <AdminSalesman />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/referrals"
          element={
            <ProtectedRoute>
              <AdminReferralPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/customers"
          element={
            <ProtectedRoute>
              <AdminCustomerList />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/permissions"
          element={
            <ProtectedRoute>
              <RolePermissionPage />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin/organizations/:orgId"
          element={
            <ProtectedRoute>
              <AdminEditCustomer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sales-analytics"
          element={
            <ProtectedRoute>
              <AdminSalesAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/usage-analytics"
          element={
            <ProtectedRoute>
              <AdminUsageAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/products"
          element={
            <ProtectedRoute>
              <AdminProductPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/organizations/:orgId/filings"
          element={
            <ProtectedRoute>
              <AdminOrganizationFilings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/today-filings"
          element={
            <ProtectedRoute>
              <AdminTodayFilings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/refund-logs"
          element={
            <ProtectedRoute>
              <AdminRefundLogs />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/announcements"
          element={
            <ProtectedRoute>
              <AdminAnnouncements />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/msic-codes"
          element={
            <ProtectedRoute>
              <AdminMsic />
            </ProtectedRoute>
          }
        />

        <Route
            path="/integration/:companyId"
            element={
            <ProtectedRoute>
                <PartnerIntegration />
            </ProtectedRoute>
            }
        />
        <Route
            path="/integration/:companyId/docs"
            element={
            <ProtectedRoute>
                <ApiDocsPage />
            </ProtectedRoute>
            }
        />

        <Route
          path="/payment-canceled"
          element={<PaymentCancel />}
        />

        <Route
          path="/payment-success"
          element={<PaymentSuccess />}
        />

        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
    </>
  );
};

export default AppRoutes;
